// /////////////////////////////////////////////////////////////
// Login Page
// /////////////////////////////////////////////////////////////
import React, { useEffect, useState } from "react";
import { loginWithGoogle, handleRedirectResult } from "../firebase/firebase";
// import { loginWithGoogle } from "../firebase/firebase";
import { useNavigate, useLocation } from "react-router-dom";
import Button from "@mui/material/Button";
import bgImage from "../assets/images/login_background_image.png";
import { APP_BACKGROUND_COLOR } from "../constants/colors";
import LoadingView from "../components/LoadingView";


import { useAuth } from "../firebase/AuthProvider";

// Google 公式の 4 色 "G" ロゴ（ブランドガイドライン準拠の SVG）
const GoogleGLogo = ({ size = 20 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg">
    <path
      fill="#4285F4"
      d="M17.64 9.2045c0-.6381-.0573-1.2518-.1636-1.8409H9v3.4814h4.8436c-.2086 1.125-.8427 2.0782-1.7959 2.7164v2.2581h2.9087c1.7018-1.5668 2.6836-3.874 2.6836-6.615z"
    />
    <path
      fill="#34A853"
      d="M9 18c2.43 0 4.4673-.806 5.9564-2.1805l-2.9087-2.2581c-.806.54-1.8368.8591-3.0477.8591-2.344 0-4.3282-1.5831-5.036-3.7104H.9574v2.3318C2.4382 15.9832 5.4818 18 9 18z"
    />
    <path
      fill="#FBBC05"
      d="M3.964 10.71c-.18-.54-.2822-1.1168-.2822-1.71s.1023-1.17.2823-1.71V4.9582H.9573A8.9965 8.9965 0 0 0 0 9c0 1.4523.3477 2.8268.9573 4.0418L3.964 10.71z"
    />
    <path
      fill="#EA4335"
      d="M9 3.5795c1.3214 0 2.5077.4541 3.4405 1.346l2.5813-2.5814C13.4636.8918 11.426 0 9 0 5.4818 0 2.4382 2.0168.9573 4.9582L3.964 7.29C4.6718 5.1627 6.656 3.5795 9 3.5795z"
    />
  </svg>
);

export default function LoginPage() {
  const [error, setError] = useState("");
  // リダイレクト方式のログイン処理中フラグ。戻り直後にログイン画面が一瞬見える事象を防ぐ
  const [redirecting, setRedirecting] = useState(
    () => sessionStorage.getItem("auth:redirecting") === "1"
  );
  const navigate = useNavigate();
  const location = useLocation();

  // PrivateRoute から渡された「ログイン前にアクセスしようとした場所」
  const from = (location.state as { from?: Location } | null)?.from;

  // -------------------------------------------------
  // Firebase : getRedirectResultの認証ロジック
  // -------------------------------------------------
  const { user } = useAuth(); // AuthContextからユーザー情報を取得

  useEffect(() => {
    // すでにログイン済みなら、元のページ（あれば）へ。無ければホームへ遷移
    if (user) {
      sessionStorage.removeItem("auth:redirecting");
      const redirectTo = from ? `${from.pathname}${from.search}` : "/";
      navigate(redirectTo, { replace: true });
    }
  }, [user, from, navigate]);

  // handleRedirectResult はエラーハンドリングのために呼び出してもよいが、
  // ユーザー判定は user オブジェクトで行う方が確実。
  useEffect(() => {
    handleRedirectResult()
      .then((result) => {
        // リダイレクト戻りでない（result が null）なら、処理中表示を解除して通常のログイン画面を表示
        if (!result) {
          sessionStorage.removeItem("auth:redirecting");
          setRedirecting(false);
        }
        // result がある場合は onAuthStateChanged で user が確定し、上の useEffect で遷移する
      })
      .catch((err) => {
        sessionStorage.removeItem("auth:redirecting");
        setRedirecting(false);
        console.error(err);
        setError(err.message || "認証エラー");
      });
  }, []);

  // ログイン処理中（リダイレクト戻りの認証確定待ち）はローディングを表示し、ログイン画面のちらつきを防ぐ
  if (redirecting) {
    return <LoadingView message="ログイン処理中..." height="100vh" />;
  }

  const handleLogin = async () => {
    try {
      await loginWithGoogle();
      // リダイレクトするのでここでの遷移は不要
    } catch (err: any) {
      setError(err.message);
    }
  };

  return (
    <div
      style={{
        display: "flex",
        justifyContent: "center", // → 水平方向に中央揃え
        alignItems: "center", // → 垂直方向に中央揃え
        height: "100vh",
        width: "100vw",
        backgroundColor: APP_BACKGROUND_COLOR, // App.css の .App と同じ背景色（画像の余白部分に表示）
        backgroundImage: `url(${bgImage})`, // インポートした変数を使用
        backgroundSize: "auto 90%",         // 高さを80%に抑えて隙間を作る
        backgroundPosition: "center 5%",
        backgroundRepeat: "no-repeat",      // 繰り返しなし
        backgroundBlendMode: "multiply",    // 画像の白地を背景色 #e5e9f1 と同化させる
      }}
    >
      <div
        style={{
          textAlign: "center"
        }}
      >
        <div>
          <Button
            disableElevation
            startIcon={<GoogleGLogo size={20} />}
            sx={{
              mt: "60%",
              width: "500px",
              height: "50px",
              px: 2,
              backgroundColor: "#FFFFFF",
              color: "#1f1f1f",
              border: "1px solid #dadce0",
              borderRadius: "24px",
              fontSize: "15px",
              fontWeight: 500,
              fontFamily: "'Roboto', 'Noto Sans JP', sans-serif",
              letterSpacing: "0.25px",
              textTransform: "none",
              boxShadow: "0 1px 2px rgba(60,64,67,0.18)",
              "& .MuiButton-startIcon": { mr: 1.5 },
              "&:hover": {
                backgroundColor: "#f7fafe",
                borderColor: "#d2e3fc",
                boxShadow: "0 1px 3px rgba(60,64,67,0.24)",
              },
            }}
            onClick={handleLogin}
          >
            Google でログイン
          </Button>
          {error && <p style={{ color: "red" }}>{error}</p>}
        </div>
      </div>
    </div>
  );
}
